/**
 * SE2 · Ethereal — light progressive enhancements for the Scentemotion visual system.
 * Run: 20260903T131518Z-scentemotion-redesign-v2
 *
 * Scope is deliberately small. Native Horizon components own product forms, variant state,
 * cart mutations, dialogs, and focus management; nothing here replaces them.
 *
 * 1. Horizontal rails: keyboard arrow navigation and edge-state attributes for scroll buttons.
 * 2. Accordion groups: optional "exclusive" behaviour when [data-se2-accordion-group] is present.
 * 3. <se2-card-variants>: the product card's tiny variant selector (2026-09-29).
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- 1. rails ---------- */
  const rails = document.querySelectorAll('[data-se2-rail]');
  for (const rail of rails) {
    const track = rail.querySelector('[data-se2-rail-track]');
    if (!track) continue;

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      rail.toggleAttribute('data-at-start', track.scrollLeft <= 1);
      rail.toggleAttribute('data-at-end', track.scrollLeft >= max - 1);
      rail.toggleAttribute('data-scrollable', max > 1);
    };

    const step = (direction) => {
      const first = track.firstElementChild;
      const amount = first ? first.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      track.scrollBy({ left: amount * direction, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    rail.querySelectorAll('[data-se2-rail-prev]').forEach((b) => b.addEventListener('click', () => step(-1)));
    rail.querySelectorAll('[data-se2-rail-next]').forEach((b) => b.addEventListener('click', () => step(1)));

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { step(1); event.preventDefault(); }
      if (event.key === 'ArrowLeft') { step(-1); event.preventDefault(); }
    });

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ---------- 2. exclusive accordion groups ---------- */
  const groups = document.querySelectorAll('[data-se2-accordion-group]');
  for (const group of groups) {
    group.addEventListener('toggle', (event) => {
      const opened = event.target;
      if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
      for (const other of group.querySelectorAll('details[open]')) {
        if (other !== opened) other.open = false;
      }
    }, true);
  }
})();

/* ---------- 3. <se2-card-variants> (2026-09-29, audit DPDP-B-07; merged COL-B-03) ----------
 * The product card's tiny variant selector, rendered by snippets/quick-add.liquid (se2_part 'variants') directly
 * above the card's price row. A radiogroup of buttons: click, or arrow keys / Home / End (selection follows focus,
 * one tab stop). Choosing an option only rewrites the card, nothing is fetched and nothing is added:
 *   - the card form's input[name="id"], so the "[+]" adds the chosen variant (product-form.js reads the form);
 *   - the price value, the struck compare-at (flat prices only) and the Subscribe & save caption;
 *   - the "[+]"'s visually hidden label, "Add <title> — <variant> to cart";
 *   - the colour name beside the dots;
 *   - the card photo, when the variant has its own (data-media-id), and every card link's ?variant=, so the photo
 *     shows the chosen colour and a tap on the card opens the PDP on it.
 * Defined here because this file loads on every page and section-rendered HTML (filters, sorting, recommendations)
 * never runs its own scripts: an element in that HTML upgrades as soon as it is inserted. */
(() => {
  if (customElements.get('se2-card-variants')) return;

  class Se2CardVariants extends HTMLElement {
    connectedCallback() {
      if (this.dataset.se2Wired) return;
      this.dataset.se2Wired = '';
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      delete this.dataset.se2Wired;
    }

    get options() {
      return Array.from(this.querySelectorAll('[role="radio"]'));
    }

    get card() {
      return this.closest('product-card') || this.closest('.se2-card') || this.parentElement;
    }

    onClick = (event) => {
      // The selector row is never a way into the product page: product-card.js navigates on any click that is not
      // on a control, and the gaps between the dots are part of this row.
      event.stopPropagation();
      const option = event.target instanceof Element ? event.target.closest('[role="radio"]') : null;
      if (option && this.contains(option)) this.select(option, false);
    };

    onKey = (event) => {
      const options = this.options;
      const current = event.target instanceof Element ? event.target.closest('[role="radio"]') : null;
      const index = current ? options.indexOf(current) : -1;
      if (index < 0 || !options.length) return;
      let next = -1;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (index + 1) % options.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (index - 1 + options.length) % options.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = options.length - 1;
          break;
        case ' ':
        case 'Enter':
          next = index;
          break;
        default:
          return;
      }
      // Rails (section 1) page on the arrow keys too; the selector keeps its keys to itself.
      event.preventDefault();
      event.stopPropagation();
      this.select(options[next], true);
    };

    /** @param {HTMLElement} option @param {boolean} focus */
    select(option, focus) {
      for (const each of this.options) {
        const on = each === option;
        each.setAttribute('aria-checked', String(on));
        each.tabIndex = on ? 0 : -1;
      }
      if (focus) option.focus();

      const data = option.dataset;
      const card = this.card;
      if (!card) return;

      const input = card.querySelector('quick-add-component[data-se2-qa] form input[name="id"]');
      if (input instanceof HTMLInputElement && data.variantId) {
        input.value = data.variantId;
        input.disabled = false;
      }

      const price = card.querySelector('.se2-card__price-value');
      if (price && data.price) price.textContent = data.price;

      const compare = card.querySelector('.se2-card__compare');
      if (compare) {
        const text = Array.from(compare.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
        if (data.compare && text) text.textContent = data.compare;
        compare.hidden = !data.compare;
      }

      const sub = card.querySelector('[data-se2-card-sub]');
      if (sub) {
        sub.textContent = data.sub || '';
        sub.hidden = !data.sub;
      }

      const label = card.querySelector('quick-add-component[data-se2-qa] .quick-add__button--add .visually-hidden');
      if (label && data.addLabel) label.textContent = data.addLabel;

      const name = this.querySelector('[data-se2-cv-name]');
      if (name && data.name) name.textContent = data.name;

      this.showVariantImage(card, data.mediaId);
      this.linkVariant(card, data.variantId);
    }

    /**
     * The chosen variant's own photo (DPDP-B-07 review). snippets/card-gallery.liquid renders every variant image as a
     * slideshow-slide[variant-image][slide-id], all but the first hidden; this mirrors product-card.js
     * #updateVariantImages: only the chosen variant's slide stays among the variant images, and the gallery moves
     * to it. A variant without its own photo leaves the gallery alone.
     * @param {Element} card @param {string | undefined} mediaId
     */
    showVariantImage(card, mediaId) {
      if (!mediaId) return;
      const slideshow = card.querySelector('slideshow-component');
      if (!slideshow) return;
      const slides = Array.from(slideshow.querySelectorAll('slideshow-slide[variant-image]'));
      if (!slides.some((slide) => slide.getAttribute('slide-id') === mediaId)) return;
      for (const slide of slides) slide.hidden = slide.getAttribute('slide-id') !== mediaId;
      // @ts-ignore slideshow-component's own API; a disabled slideshow simply shows its first visible slide
      if (typeof slideshow.select === 'function') slideshow.select({ id: mediaId }, undefined, { animate: false });
    }

    /**
     * Every link on the card opens the chosen variant (DPDP-B-07 review): product-card.js applyVariantToLinks
     * rewrites the stretched card link, the gallery link and the native title link; the SE2 name link is set here.
     * @param {Element} card @param {string | undefined} variantId
     */
    linkVariant(card, variantId) {
      if (!variantId) return;
      const productCard = card.closest('product-card') || card;
      // @ts-ignore product-card's public method
      if (typeof productCard.applyVariantToLinks === 'function') productCard.applyVariantToLinks(variantId);
      for (const link of card.querySelectorAll('a.se2-card__name-link, a.product-card__link, a[ref="cardGalleryLink"]')) {
        if (!(link instanceof HTMLAnchorElement) || !link.getAttribute('href')) continue;
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', variantId);
        link.href = url.toString();
      }
    }
  }

  customElements.define('se2-card-variants', Se2CardVariants);
})();
